import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  IconButton,
  Tooltip,
} from "@mui/material";
import { Delete as DeleteIcon } from "@mui/icons-material";
import { apiClient } from "../../utils/apiClient";
import { format } from "date-fns";

interface PipelineResultListItem {
  id: string;
  name: string | null;
  testSetName: string | null;
  workflowStatus: string;
  processedCount: number;
  totalCount: number;
  runAt: string;
  emailIds: string[];
}

interface EmailAutomationPipelineResultsListProps {
  configId?: string;
  refreshTrigger?: number;
  onResultDeleted?: () => void;
}

const getStatusChip = (status: string) => {
  switch (status) {
    case "completed":
      return <Chip label="Completed" color="success" size="small" />;
    case "running":
      return <Chip label="Running" color="info" size="small" />;
    case "pending":
      return <Chip label="Pending" color="warning" size="small" />;
    case "failed":
      return <Chip label="Failed" color="error" size="small" />;
    default:
      return <Chip label={status} size="small" />;
  }
};

export function EmailAutomationPipelineResultsList({
  configId,
  refreshTrigger = 0,
  onResultDeleted,
}: EmailAutomationPipelineResultsListProps) {
  const navigate = useNavigate();
  const [results, setResults] = useState<PipelineResultListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState<string | null>(null);

  const loadResults = async () => {
    try {
      setLoading(true);
      const res = await apiClient.post<{ results: PipelineResultListItem[] }>(
        `/admin/email-automation/results/list`,
        configId ? { configId } : {}
      );
      setResults(res.results);
    } catch (error) {
      console.error("Error loading results:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadResults();
  }, [configId, refreshTrigger]);

  const handleViewResult = (resultId: string) => {
    navigate(`/admin/email-automation/result/${resultId}`);
  };

  const handleDeleteResult = async (
    resultId: string,
    event: React.MouseEvent
  ) => {
    event.stopPropagation();
    if (!window.confirm("Are you sure you want to delete this result?")) return;

    setDeleting(resultId);
    try {
      await apiClient.delete(`/admin/email-automation/results/delete`, {
        id: resultId,
      });
      setResults((prev) => prev.filter((r) => r.id !== resultId));
      onResultDeleted?.();
    } catch (error) {
      console.error("Error deleting result:", error);
    } finally {
      setDeleting(null);
    }
  };

  if (loading) {
    return (
      <Paper sx={{ p: 3, mt: 3 }}>
        <Box display="flex" justifyContent="center" alignItems="center" py={4}>
          <CircularProgress size={24} />
        </Box>
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 3, mt: 3 }}>
      <Typography variant="h6" gutterBottom>
        Pipeline Run Results
      </Typography>

      {results.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
          No pipeline runs yet. Use "Run Pipeline" to process emails.
        </Typography>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Email Set</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Emails</TableCell>
                <TableCell>Run Date</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {results.map((result) => (
                <TableRow
                  key={result.id}
                  hover
                  sx={{ cursor: "pointer" }}
                  onClick={() => handleViewResult(result.id)}
                >
                  <TableCell>
                    <Typography variant="body2">
                      {result.name || (
                        <Typography
                          component="span"
                          variant="body2"
                          color="text.secondary"
                          sx={{ fontStyle: "italic" }}
                        >
                          Untitled
                        </Typography>
                      )}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {result.testSetName || (
                      <Typography
                        component="span"
                        variant="body2"
                        color="text.secondary"
                      >
                        Manual IDs
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>{getStatusChip(result.workflowStatus)}</TableCell>
                  <TableCell>
                    {result.processedCount}/{result.totalCount}
                  </TableCell>
                  <TableCell>
                    {format(new Date(result.runAt), "MMM d, yyyy HH:mm")}
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Delete Result">
                      <IconButton
                        size="small"
                        onClick={(e) => handleDeleteResult(result.id, e)}
                        disabled={deleting === result.id}
                        color="error"
                      >
                        {deleting === result.id ? (
                          <CircularProgress size={16} />
                        ) : (
                          <DeleteIcon fontSize="small" />
                        )}
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Paper>
  );
}
